import {
    IMedicinePrescription,
    IPatientAllergy,
    IPatientSurgeryAndProcedure
} from "@curefit/ehr-common";
import { PatientPersonalProfile } from "@curefit/ups-common"
import { IPrescription, ICenter, IGeneralExamination } from "@curefit/care-common"
import { IPatientDetail, IAppointmentScheduleResponse, ITwilioAccessToken } from "@curefit/care-common";
import { IAlbusApis } from "./IAlbusApis";
import ApiUtils from "../utils/ApiUtils"
import DoctorProfile from "../models/IDoctorProfile"
import IAccountData from "../models/IAccountData"
import { IAppointmentScheduleRequestParams } from "../models/IAppointmentScheduleRequestParams"
import Vital from "../models/staticData/IVital"
import PatientVital from "../models/IPatientVital"
import { IPatientVitalResponse } from "../models/IPatientVitalResponse"
import IPatientDetailAndMedicalCondition from "../models/IPatientDetailAndMedicalCondition"
import { IStaticDataResponse } from "../models/staticData/IStaticDataResponse"
import { IStaticDataRequestParams } from "../models/request/IStaticDataRequestParams"
import ICurrentMedication from "../models/ICurrentMedication"
import { IEHRSavePatientMedicalProfile } from "../models/patients/IEHRSavePatientMedicalProfile"
import { IAppointmentStatusRequestParam } from "../models/IAppointmentStatusRequestParam"
import IResourceRequestParams from "../models/IResourceRequestParams"
import { ICommAccessTokenRequestParams } from "../models/ICommAccessTokenRequestParams"
import IDiagnosticSuggestions from "../models/IDiagnosticSuggestions"
import IHabit from "../models/staticData/IHabit"
import IMedicineSuggestions from "../models/IMedicineSuggestions"
import IDifferentialDiagnosis from "../models/IDifferentialDiagnosis"
import { TypedJSON } from "typedjson-npm"
import RoutineAttribute from "../models/staticData/RoutineAttribute"
import IPatientCondition from "../models/IPatientCondition"
import { IConsultationUpdateParams } from "../models/appointment/IConsultationUpdateParams"
import { IOrderEvent } from "../models/order/IOrderEvent"
import IDiagnosticOrderRequestParams from "../models/IDiagnosticOrderRequestParams";
import ILabTestMaster from "../models/ILabTestMaster";
import { ITypeFormResponse } from "../models/ITypeFormResponse";
import { AppConstants } from "../utils/AppConstants";
import IUnreadMsgInfo from "../models/twilio/IUnreadMsgInfo";
import { IUnreadMsgInfoRequestParams } from "../models/twilio/IUnreadMsgInfoRequestParams";
import ModelFactory from "../models/ModelFactory";
import AppUtils from "../utils/AppUtils";
import ISearchDoctor from "../models/ISearchDoctor";
import IPrivateNotes from "../models/IPrivateNotes";
import IDoctorImageSignedUrl from "../models/IDoctorImageSignedUrl";
import { AlbusUrlConfigs } from "./AlbusUrlConfigs";
import { IAddMetric } from "@curefit/metrics/dist/src/models/MetricModels";
import { ApiManager } from "./ApiManager";
import AppointmentScheduleRequestParamsBuilder from "../builder/AppointmentScheduleRequestParamsBuilder";
import { IRosterRequest } from "../models/Roster/IRosterRequest";
import {
    IResourceSchedule,
    ResourceScheduleResponse,
    IWeeklySchedule,
    IDeleteAgentRoster
} from "../models/Roster/RosterResponse";
import { IAddRosterRequest } from "../models/Roster/IAddRosterRequest";
import * as moment from "moment";
import { IConsultationCountResponse } from "../models/IConsultationCountResponse";
import { ConsultationFeedbackPayload } from "../containers/ConsultationFeedbackFormContainer/ConsultationFeedbackForm";
import { ConsentParam } from "../builder/ConsentParamBuilder";
import { IRecomTemplate } from "../models/IRecoTemplates";
import { GlucoseLevelCode, IGlucoseLevels } from "../models/IGlucoseLevels";
import { ICgmReport } from "../models/ICgmReport";
import { ICgmStats } from "../models/ICgmStats";

export default class AlbusApis implements IAlbusApis {
    static addSymptomForReview(name: any) {
        return ApiUtils.apiPostRequest(AlbusUrlConfigs.getAddSymptomForReviewUrl(), JSON.stringify({ name }))
    }
    getPatientMedicalProfile(patientId: number): Promise<IEHRSavePatientMedicalProfile> {
        throw new Error("Method not implemented.");
    }
    getPhysicalAssessmentProfile(patientId: number, appointmentId: number, patientMedicalProfile: any): Promise<any> {
        throw new Error("Method not implemented.");
    }
    submitConsultationFeedback(feedbackData: ConsultationFeedbackPayload): Promise<any> {
        return ApiUtils.apiPostRequest(AlbusUrlConfigs.getConsultationFeedbackUrl(), JSON.stringify({
            feedbackForm: feedbackData
        }), {
            "Content-Type": "application/json"
        });
    }
    createGetLabTestsUrl(input: string): Promise<any> {
        throw new Error("Method not implemented.");
    }
    savePatientDiagnosticLabTests(metric: import("@curefit/metrics-common").IAddMetric): Promise<any> {
        throw new Error("Method not implemented.");
    }

    getAccountData(): Promise<IAccountData> {
        return null
    }

    getPatientCompleteDetailsById(patientId: number, numOfDays: number, startDateEpoch: number): Promise<IPatientDetailAndMedicalCondition> {
        let numberOfDays = numOfDays !== undefined ? numOfDays : AppConstants.FETCH_MEDICAL_PROFILE_FOR_NUMBER_OF_DAYS_DEFAULT;
        let startEpoch = startDateEpoch !== undefined ? startDateEpoch : new Date().getTime();
        let currUrl: string = AlbusUrlConfigs.createGetPatientCompleteDetailsByIdUrl(patientId, numberOfDays, startEpoch);
        return ApiUtils.apiGetRequest(currUrl)
    }

    getPatientProfileById(patientId: number): Promise<IPatientDetail[]> {
        let currUrl: string = AlbusUrlConfigs.createGetPatientByIdUrl(patientId)
        return ApiUtils.apiGetRequest(currUrl)
    }

    getDoctorProfile(searchParams: ISearchDoctor): Promise<DoctorProfile[]> {
        let currUrl: string = AlbusUrlConfigs.getDoctorSearchURL(searchParams);
        return ApiUtils.apiGetRequest(currUrl)
    }

    saveDoctor(doctorProfile: DoctorProfile): Promise<DoctorProfile> {
        let currUrl: string = AlbusUrlConfigs.createGetDoctorProfileByIdUrl(doctorProfile.id)
        return ApiUtils.apiGetRequest(currUrl)
    }

    getSurgeryAndProcedureHistoryData(patientId: number): Promise<IPatientSurgeryAndProcedure[]> {
        let currUrl: string = AlbusUrlConfigs.createGetSurgeryAndProcedureHistoryURL(patientId)
        return ApiUtils.apiGetRequest(currUrl)
    }

    saveSurgeryAndProcedureHistoryData(patientId: number, surgeryToSave: IPatientSurgeryAndProcedure[]): Promise<IPatientSurgeryAndProcedure[]> {
        let currUrl: string = AlbusUrlConfigs.createSaveSurgeryAndProcedureHistoryURL(patientId)
        return ApiUtils.apiPostRequest(currUrl, JSON.stringify(surgeryToSave))
    }

    getAllergyData(patientId: number): Promise<IPatientAllergy[]> {
        let currUrl: string = AlbusUrlConfigs.createGetAllergyHistoryURL(patientId)
        return ApiUtils.apiGetRequest(currUrl)
    }

    saveAllergyData(patientId: number, allergyToSave: IPatientAllergy[]): Promise<IPatientAllergy[]> {
        let currUrl: string = AlbusUrlConfigs.createSaveAllergyHistoryURL(patientId)
        return ApiUtils.apiPostRequest(currUrl, JSON.stringify(allergyToSave))
    }

    getHabitData(patientId: number): Promise<IHabit[]> {
        let currUrl: string = AlbusUrlConfigs.createGetHabitHistoryURL(patientId)
        return ApiUtils.apiGetRequest(currUrl)
    }

    saveHabitData(patientId: number, habitToSave: IHabit[]): Promise<IHabit[]> {
        let currUrl: string = AlbusUrlConfigs.createSaveHabitHistoryURL(patientId)
        return ApiUtils.apiPostRequest(currUrl, JSON.stringify(habitToSave))
    }

    getCurrentMedicationData(patientId: number): Promise<ICurrentMedication[]> {
        let currUrl: string = AlbusUrlConfigs.createGetCurrentMedicationURL(patientId)
        return ApiUtils.apiGetRequest(currUrl)
    }

    saveCurrentMedicationData(patientId: number, medsToSave: IMedicinePrescription[]): Promise<IMedicinePrescription[]> {
        let currUrl: string = AlbusUrlConfigs.createSaveCurrentMedicationURL(patientId)
        return ApiUtils.apiPostRequest(currUrl, JSON.stringify(medsToSave))
    }

    getComorbidConditionData(patientId: number): Promise<IPatientCondition[]> {
        let currUrl: string = AlbusUrlConfigs.createGetComorbidConditionsURL(patientId)
        return ApiUtils.apiGetRequest(currUrl)
    }

    saveComorbidConditionData(patientId: number, conditions: IPatientCondition[]): Promise<IPatientCondition[]> {
        let currUrl: string = AlbusUrlConfigs.createSaveComorbidConditionsURL(patientId)
        return ApiUtils.apiPostRequest(currUrl, JSON.stringify(conditions))
    }

    getGeneralExamination(): Promise<IGeneralExamination[]> {
        let currUrl: string = AlbusUrlConfigs.createGetGeneralExaminationUrl()
        return ApiUtils.apiGetRequest(currUrl)
    }

    getVitals(): Promise<Vital[]> {
        let currUrl: string = AlbusUrlConfigs.createGetVitalsUrl()
        return ApiUtils.apiGetRequest(currUrl)
    }

    saveVitals(patientId: number, patientVital: PatientVital[]): Promise<any> {
        let currUrl: string = AlbusUrlConfigs.createPostVitalsUrl(patientId)
        return ApiUtils.apiPostRequest(currUrl, JSON.stringify(patientVital))
    }

    getPatientVitals(patientId: number): Promise<IPatientVitalResponse[]> {
        let currUrl: string = AlbusUrlConfigs.createGetAllVitalForPatientUrl(patientId)
        return ApiUtils.apiGetRequest(currUrl)
    }

    getPatientLabTests(patientId: number, requestParams: IDiagnosticOrderRequestParams): Promise<ILabTestMaster[]> {
        let currUrl: string = AlbusUrlConfigs.createGetAllLabTestsForPatientUrl(patientId, requestParams)
        return ApiUtils.apiGetRequest(currUrl)
    }

    getAppointments(requestParams: IAppointmentScheduleRequestParams): Promise<IAppointmentScheduleResponse[]> {
        let currUrl: string = AlbusUrlConfigs.createGetAppointmentsUrl(requestParams)
        return ApiUtils.apiGetRequest(currUrl)
    }

    getAppointmentById(appointmentId: number): Promise<IAppointmentScheduleResponse> {
        let currUrl: string = AlbusUrlConfigs.createGetAppointmentDetailsById(appointmentId)
        return ApiUtils.apiGetRequest(currUrl)
    }

    getTwilioContext(requestParams: ICommAccessTokenRequestParams): Promise<ITwilioAccessToken[]> {
        let currUrl: string = AlbusUrlConfigs.createGetTwilioContextUrl(requestParams)
        return ApiUtils.apiGetRequest(currUrl)
    }

    getVideoContext(appointmentId: number): Promise<any> {
        let currUrl: string = AlbusUrlConfigs.createGetVideoContextUrl(appointmentId)
        return ApiUtils.apiGetRequest(currUrl)
    }

    getStaticData(staticDataRequestParams: IStaticDataRequestParams): Promise<IStaticDataResponse> {
        let currUrl: string = AlbusUrlConfigs.createGetStaticDataUrl(staticDataRequestParams)
        return ApiUtils.apiGetRequest(currUrl)
    }

    savePatientPrescription(appointmentId: number, prescription: IPrescription): Promise<IPrescription> {
        let currUrl: string = AlbusUrlConfigs.createPatientPrescriptionUrl(appointmentId)
        return ApiUtils.apiPostRequest(currUrl, JSON.stringify(prescription))
    }

    saveDifferentialDiagnosis(appointmentId: number, differentialDiagnosis: IDifferentialDiagnosis): Promise<any> {
        let currUrl: string = AlbusUrlConfigs.createSaveDifferentialDiagnosisUrl(appointmentId)
        return ApiUtils.apiPostRequest(currUrl, JSON.stringify(differentialDiagnosis))
    }

    savePrivateNotes(appointmentId: number, privateNotes: IPrivateNotes) {
        let currUrl: string = AlbusUrlConfigs.createSavePrivateNotesUrl(appointmentId);
        return ApiUtils.apiPostRequest(currUrl, JSON.stringify(privateNotes));
    }

    fetchCaseSummary(appointmentId: number) {
        let currUrl: string = AlbusUrlConfigs.fetchCaseSummary(appointmentId);
        return ApiUtils.apiGetRequest(currUrl);
    }

    updateAppointmentStatus(
        bookingId: number,
        appointmentId: number,
        requestParams: IAppointmentStatusRequestParam
    ): Promise<any> {
        let currUrl: string = AlbusUrlConfigs.createUpdateAppointmentStatusUrl(
            bookingId,
            appointmentId
        );
        return ApiUtils.apiPostRequest(
            currUrl,
            JSON.stringify(requestParams),
            true,
            AppConstants.UPDATE_APPOINTMENT_STATUS_TIMEOUT_IN_SECONDS
        );
    }

    updateAppointment(bookingId: number, appointmentId: number, requestParams: IConsultationUpdateParams): Promise<IAppointmentScheduleResponse> {
        let currUrl: string = AlbusUrlConfigs.createUpdateAppointmentStatusUrl(bookingId, appointmentId)
        return ApiUtils.apiPostRequest(currUrl, JSON.stringify(requestParams))
    }

    getIsActiveCovidPack(userId: number) {
        return ApiUtils.apiGetRequest(AlbusUrlConfigs.getIsActiveCovidPack(userId));
    }

    getCovidMetrics() {
        return ApiUtils.apiPostRequest(AlbusUrlConfigs.getCovidMetricIds(), "{}");
    }

    getCovidVitals(startEpoch: number, endEpoch: number, metricIds: string[], patientId: number, userId: string) {
        return ApiUtils.apiGetRequest(AlbusUrlConfigs.getCovidVitals(startEpoch, endEpoch, metricIds, patientId, userId))
    }

    /* TYPE AHEAD APIS START */
    getMedicineByName(medicineName: string): Promise<IMedicineSuggestions[]> {
        let currUrl: string = AlbusUrlConfigs.createGetMedicinesUrl(medicineName)
        return ApiUtils.apiGetRequest(currUrl)
    }

    getMedicineByMolecule(genericName: string): Promise<IMedicineSuggestions[]> {
        let currUrl: string = AlbusUrlConfigs.createGetMedicineByMoleculesUrl(genericName)
        return ApiUtils.apiGetRequest(currUrl)
    }

    getSymptomsByName(symptomName: string): Promise<any> {
        let currUrl: string = AlbusUrlConfigs.createGetSymptomsByNameUrl(symptomName)
        return ApiUtils.apiGetRequest(currUrl)
    }

    getDiagnosticSuggestions(input: string): Promise<IDiagnosticSuggestions[]> {
        let currUrl: string = AlbusUrlConfigs.createGetDiagnosticsUrl(input)
        return ApiUtils.apiGetRequest(currUrl)
    }

    getDiseaseByName(disease: string): Promise<any> {
        let currUrl: string = AlbusUrlConfigs.createGetDiseasesUrl(disease)
        return ApiUtils.apiGetRequest(currUrl)
    }

    getLifeStyleByName(lifestyle: string): Promise<any> {
        let currUrl: string = AlbusUrlConfigs.createGetLifeStyleUrl(lifestyle)
        return ApiUtils.apiGetRequest(currUrl)
    }

    getAllergiesByName(allergy: string): Promise<any> {
        let currUrl: string = AlbusUrlConfigs.createGetAllergiesUrl(allergy)
        return ApiUtils.apiGetRequest(currUrl)
    }

    getFitnessActivities(activity: string): Promise<any> {
        let currUrl: string = AlbusUrlConfigs.createGetFitnessActivitiesUrl(activity);
        return ApiUtils.apiGetRequest(currUrl);
    }

    /* TYPE AHEAD APIS END */

    getCenterDetails(centerId: number): Promise<ICenter> {
        let currUrl: string = AlbusUrlConfigs.createGetCenterDetailsUrl(centerId)
        return ApiUtils.apiGetRequest(currUrl)
    }

    getResources(resourceRequestParams: IResourceRequestParams) {
        let currUrl: string = AlbusUrlConfigs.createGetResourcesUrl(resourceRequestParams)
        return ApiUtils.apiGetRequest(currUrl)
    }
    getPatientPastSession(patientId: number, subSectionKey: string) {
        let currUrl: string = AlbusUrlConfigs.getPatientPastSessionsURL(patientId, subSectionKey);
        return ApiUtils.apiGetRequest(currUrl);
    }

    uploadPrescription(id: number, file: File, type: string): Promise<any> {
        let currUrl: string = AlbusUrlConfigs.createUploadPrescriptionUrl(id, type)
        return ApiUtils.apiFormDataRequest(currUrl, file)
    }

    uploadPlan(id: number, file: File, type: string): Promise<any> {
        let currUrl: string = AlbusUrlConfigs.createUploadPlanUrl(id, type)
        return ApiUtils.apiFormDataRequest(currUrl, file)
    }

    emailPrescription(bookingId: number, appointmentId: number): Promise<any> {
        let currUrl: string = AlbusUrlConfigs.createSendPrescriptionUrl(bookingId, appointmentId)
        return ApiUtils.apiGetRequest(currUrl)
    }

    emailPlan(appointmentId: number, curefitUserId: string, bookingId: number): Promise<any> {
        let currUrl: string = AlbusUrlConfigs.createSendPlanUrl(appointmentId, curefitUserId, bookingId)
        return ApiUtils.apiGetRequest(currUrl)
    }

    updatePatientPersonalInfo(id: string, personalInfo: PatientPersonalProfile): Promise<PatientPersonalProfile> {
        let currUrl: string = AlbusUrlConfigs.createPatientPersonalUrl(id)
        console.log("UPDATE_PATIENT_PROILE< START", currUrl);
        return ApiUtils.apiPostRequest(currUrl, JSON.stringify(personalInfo))
    }

    fetchPatientPersonalInfo(id: string): Promise<PatientPersonalProfile> {
        let currUrl: string = AlbusUrlConfigs.createPatientPersonalUrl(id)
        return new Promise((resolve: any, reject: any) => {
            ApiUtils.apiGetRequest(currUrl, true, AppConstants.FETCH_PATIENT_PERSONAL_PROFILE_TIMEOUT_IN_SECONDS).then((jsonData: any) => {
                let resource: PatientPersonalProfile = TypedJSON.parse(jsonData)
                resolve(Object.assign({}, resource))
            }).catch(error => {
                reject(error)
            })
        })
    }

    fetchStaticRoutineAttr(): Promise<RoutineAttribute> {
        let currUrl: string = AlbusUrlConfigs.createStaticRoutineAttrURL()
        return new Promise((resolve: any, reject: any) => {
            ApiUtils.apiGetRequest(currUrl).then((jsonData: any) => {
                let attributes: RoutineAttribute = TypedJSON.parse(jsonData, RoutineAttribute)
                resolve(Object.assign({}, attributes))
            }).catch(error => {
                reject(error)
            })
        })
    }

    logOrderEvent(orderEvent: IOrderEvent): Promise<IOrderEvent> {
        let logOrderEventUrl: string = AlbusUrlConfigs.createLogOrderEventUrl(orderEvent)
        return ApiUtils.apiPostRequest(logOrderEventUrl, JSON.stringify(orderEvent));
    }

    fetchPatientTypeFormData(patientId: number): Promise<ITypeFormResponse> {
        let currUrl: string = AlbusUrlConfigs.createGetTypeFormDataUrl(patientId)
        return ApiUtils.apiGetRequest(currUrl, true)
    }

    fetchUnreadMessageInfo(doctorId: string): Promise<IUnreadMsgInfo[]> {
        let reqParams: IUnreadMsgInfoRequestParams = {
            fromTime: AppUtils.getFetchUnreadMessageFromTime(),
            toTime: AppUtils.getFetchUnreadMessageToTime()
        }
        let currUrl: string = AlbusUrlConfigs.createFetchUnreadMessageInfoDataUrl(doctorId, reqParams);
        return ApiUtils.apiGetRequest(currUrl, true)
    }

    updateConsumptionHorizon(channelSid: string, memberUniqueIdentity: string, lastConsumedMessageIndex: number,
        lastMessageConsumedTimestamp: number): Promise<any> {
        let req = {
            channelSid,
            memberUniqueIdentity,
            lastConsumedMessageIndex,
            lastMessageConsumedTimestamp
        }
        return ApiUtils.apiPostRequest(AlbusUrlConfigs.createUpdateConsumptionHorizonUrl(), JSON.stringify(req));
    }

    addTwilioRoomEventLog(reqBody: any, headers?: any): Promise<any> {
        return ApiUtils.apiPostRequest(AlbusUrlConfigs.addTwilioRoomEventLog(), JSON.stringify(reqBody), headers);
    }

    fetchConsultationProductName(categoryCode: string, productCode: string): Promise<any> {
        return ApiUtils.apiGetRequest(AlbusUrlConfigs.createFetchConsultationProductUrl(categoryCode, productCode))
    };

    searchPatients(curefitUserId: string): Promise<any> {
        return ApiUtils.apiGetRequest(AlbusUrlConfigs.searchPatients(curefitUserId))
    }

    getDoctorSignatureImageDownloadPreSignedUrl(doctorId: number): Promise<IDoctorImageSignedUrl> {
        return ApiUtils.apiGetRequest(AlbusUrlConfigs.getDoctorSignatureImageDownloadPreSignedUrl(doctorId))
    }

    searchDoctorPatients(doctorId: any, query: any) {
        return ApiUtils.apiGetRequest(AlbusUrlConfigs.getSearchDoctorPatientsUrl(doctorId, query))
    }

    addSymptomForReview(name: string): Promise<any> {
        return ApiUtils.apiPostRequest(AlbusUrlConfigs.getAddSymptomForReviewUrl(), JSON.stringify({ name }))
    }

    fetchAppointmentChatContextAndOrderAssetsById(appointmentId: number, isPackInfoRequired?: boolean): Promise<IAppointmentScheduleResponse> {
        let reqBuilder: AppointmentScheduleRequestParamsBuilder = new AppointmentScheduleRequestParamsBuilder();
        reqBuilder.setAppointmentId(appointmentId)
            .setAppointmentContextRequired()
            .setDoctorRequired()
            .setConsultationProductRequired()
            .setPatientRequired()
            .setIsAvailableDoctorActionRequired()
            .setOrderAssetsRequired()
            .setPrescriptionEditContextRequired()
            .setPackInfoRequired(isPackInfoRequired);
        return new Promise((resolve, reject) => {
            this.getAppointments(reqBuilder.build()).then((res: IAppointmentScheduleResponse[]) => {
                resolve(res[0])
            }).catch((err: any) => {
                reject(err)
            });
        })
    };

    // fetchRoster(params: IRosterRequest): Promise<IResourceSchedule[]> {
    //     let url: string = AlbusUrlConfigs.gerRosterUrl(params);
    //     return new Promise((resolve, reject) => {
    //         ApiUtils.apiGetRequest(url)
    //             .then(res => {
    //                 res = res[0];
    //                 const parsedResposne: IResourceSchedule[] = res.doctorSchedules.map((schedule: IWeeklySchedule) => {
    //                     return {
    //                         shiftSchedule: {},
    //                         id: schedule.,
    //                         resourceType: res.type,
    //                         resourceId: res.id,
    //                         workingDayDate: schedule.workingDate,
    //                         scheduleStatus: schedule.deletedAt === null ? "INACTIVE" : "ACTIVE",
    //                         centerId: schedule.centerId,
    //                         startTime: moment(schedule.workingStartTime).format("HH:mm"),
    //                         endTime: moment(schedule.workingEndTime).format("HH:mm"),
    //                         workingHrs: null as any,
    //                         availableOffline: schedule.availableOffline,
    //                         availableOnline: schedule.availableOnline,
    //                         resourceDeallocationScheduleList: [] as any
    //                     }
    //                 })

    //                 resolve(parsedResposne);
    //             })
    //             .catch(e => reject(e));
    //     })

    // }
    fetchRoster(params: IRosterRequest): Promise<IResourceSchedule[]> {
        let url: string = AlbusUrlConfigs.gerRosterUrl(params);
        return ApiUtils.apiGetRequest(url);
    }

    updateRoster(params: IAddRosterRequest[]): Promise<ResourceScheduleResponse> {
        let url: string = AlbusUrlConfigs.updateRosterUrl();
        return ApiUtils.apiPutRequest(url, JSON.stringify(params));
    }

    getConsultationsCount(doctorId: string | number, fromTimeEpoch: number): Promise<IConsultationCountResponse> {
        return ApiUtils.apiGetRequest(AlbusUrlConfigs.getConsultationsCountUrl(doctorId, fromTimeEpoch))
    }

    getDocPreSignedUrlForUpload(referenceType: string, referenceId: number): Promise<any> {
        return ApiUtils.apiGetRequest(AlbusUrlConfigs.getPreSignedUrlForUpload(referenceType, referenceId.toString()));
    }

    getDocPreSignedUrlForDownload(referenceType: string, referenceId: string, s3KeyName: string): Promise<any> {
        return ApiUtils.apiGetRequest(AlbusUrlConfigs.getDocPreSignedUrlForDownload(referenceType, referenceId, s3KeyName));
    }

    fetchAccessPolicyForCurrentUser(principleReferenceEntity: string, ownerReferenceEntity: string, ownerReferenceEntityId: string, resourceType: string, principleReferenceEntityId: string): Promise<any> {
        let url: string = AlbusUrlConfigs.getAccessPolicyFetchUrl(principleReferenceEntity, ownerReferenceEntity, ownerReferenceEntityId, resourceType, principleReferenceEntityId);
        // this api returns 204 response with 
        return fetch(url).then(res => res.text());
    }

    setAccessPolicyForCurrentUser(params: ConsentParam): Promise<any> {
        let url: string = AlbusUrlConfigs.getAccessPolicyPutUrl();
        return ApiUtils.apiPutRequest(url, JSON.stringify(params));
    }
    fetchRecomTemplates = (doctorId: number, count: number): Promise<IRecomTemplate> => {
        const url = AlbusUrlConfigs.getRecoTreatementPlanFetchUrl(doctorId, count);
        return ApiUtils.apiGetRequest(url, true);
    }

    deleteAgentRoster = (jsonBody: IDeleteAgentRoster): Promise<ResourceScheduleResponse> => {
        return ApiUtils.apiPostRequest(AlbusUrlConfigs.getDeleteAgentRosterUrl(), JSON.stringify(jsonBody))
    }

    fetchGlucoseLevels(levelCode?: GlucoseLevelCode): Promise<IGlucoseLevels[]> {
        return ApiUtils.apiGetRequest(AlbusUrlConfigs.getFetchGlucoseLevelsUrl(levelCode))
    }

    fetchCgmReport(userId: string): Promise<ICgmReport> {
        return ApiUtils.apiGetRequest(AlbusUrlConfigs.getFetchCgmReportUrl(userId))
    }

    fetchCgmStats(userId: string): Promise<ICgmStats> {
        return ApiUtils.apiGetRequest(AlbusUrlConfigs.getFetchCgmStatsUrl(userId))
    }
}